import { forwardRef, type InputHTMLAttributes } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

// Unchecked: `border-control` (≥3:1). Checked: yellow fill with a `brand-700`
// edge so the state stays visible on white (yellow alone is 1.34:1).
// Radius: rounded-md (6px) — the one exception to the 8px minimum, an 8px
// corner on a 16px box would read as a radio button.

interface CheckboxMarkProps {
	checked: boolean;
	size?: number;
	className?: string;
}

/**
 * Visual-only checkbox for rows that are themselves the clickable control
 * (the row is the button; this just shows the state).
 */
export function CheckboxMark({ checked, size = 16, className }: CheckboxMarkProps) {
	return (
		<span
			aria-hidden="true"
			className={cn(
				"inline-flex shrink-0 items-center justify-center rounded-md border-2 transition-colors",
				checked
					? "border-brand-700 bg-brand-400 text-text-on-brand"
					: "border-border-control bg-bg-surface",
				className,
			)}
			style={{ width: size, height: size }}
		>
			{checked && <Check size={Math.max(size - 6, 8)} strokeWidth={3} />}
		</span>
	);
}

/** Native checkbox styled to the same rules. */
export const Checkbox = forwardRef<
	HTMLInputElement,
	Omit<InputHTMLAttributes<HTMLInputElement>, "type">
>(({ className, ...props }, ref) => (
	<input
		ref={ref}
		type="checkbox"
		className={cn(
			"h-4 w-4 shrink-0 cursor-pointer accent-brand-400",
			"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg-surface",
			"disabled:cursor-not-allowed",
			className,
		)}
		{...props}
	/>
));

Checkbox.displayName = "Checkbox";
